class InfiniteList extends HTMLElement {
  constructor() {
    super();
    this.productsContainerSelector = this.getAttribute('data-products-container');
    this.paginationSelector = this.getAttribute('data-pagination-selector');
    this.nextPageSelector = this.getAttribute('data-next-page-selector');
    this.productsContainerEl = this.querySelector(this.productsContainerSelector);
    this.paginationEl = this.querySelector(this.paginationSelector);
    this.loadingIndicator = this.querySelector('.loading-overlay-wrapper');
    this.pageUrl = window.location.pathname;
    this.pageIndex = 2;
    this.totalPages = parseInt(this.getAttribute('data-total-pages')) || 1;
    this.loadedPages = new Set([1]); // Page 1 is already loaded
    this.loadPromises = [];

    if (!this.productsContainerEl || !this.paginationEl) {
      console.error('Wrong configuration for infinite scroll');
      return;
    }

    // Hide pagination
    this.paginationEl.style.visibility = 'hidden';
    
    // Start loading pages asynchronously after a short delay to allow page to be interactive
    setTimeout(() => {
      this.loadAllPagesAsync();
    }, 100);
  }

  /**
   * Load all pages asynchronously without blocking
   */
  loadAllPagesAsync() {
    if (this.pageIndex > this.totalPages) {
      // All pages loaded, dispatch completion event
      this.dispatchAllPagesLoaded();
      return;
    }

    // Show loading indicator
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.remove('hidden');
    }

    // Load all remaining pages in parallel (with a small delay between each to avoid overwhelming the server)
    const loadPromises = [];
    for (let i = this.pageIndex; i <= this.totalPages; i++) {
      const pageNum = i;
      const delay = (pageNum - this.pageIndex) * 50; // Stagger requests by 50ms
      
      const promise = new Promise((resolve) => {
        setTimeout(() => {
          this.loadPage(pageNum).then(resolve).catch((error) => {
            console.error(`Error loading page ${pageNum}:`, error);
            resolve(); // Continue even if one page fails
          });
        }, delay);
      });
      
      loadPromises.push(promise);
    }

    // Wait for all pages to load, then dispatch completion event
    Promise.all(loadPromises).then(() => {
      if (this.loadingIndicator) {
        this.loadingIndicator.classList.add('hidden');
      }
      this.dispatchAllPagesLoaded();
    });
  }

  /**
   * Load a specific page
   */
  loadPage(pageIndex) {
    if (this.loadedPages.has(pageIndex)) {
      return Promise.resolve();
    }

    const pageUrl = this.pageUrl + '?page=' + pageIndex;
    return fetch(pageUrl)
      .then(result => {
        if (!result.ok) {
          throw new Error(result.statusText);
        }
        return result.text();
      })
      .then((html) => {
        this.handleNextPage(html, pageIndex);
        this.loadedPages.add(pageIndex);
        console.log(`Page ${pageIndex} loaded`);
        return Promise.resolve();
      });
  }

  /**
   * Handle loaded page content
   */
  handleNextPage(nextPageContent, pageIndex) {
    const nextPageHtml = new DOMParser().parseFromString(nextPageContent, 'text/html');
    const newRows = nextPageHtml.querySelectorAll('tbody tr.variant-item');
    const tableBody = document.querySelector('.quick-order-list__table tbody');
    
    if (tableBody && newRows.length > 0) {
      // Use DocumentFragment for better performance
      const fragment = document.createDocumentFragment();
      newRows.forEach((row) => {
        const statusRow = row.nextElementSibling;
        fragment.appendChild(row.cloneNode(true));
        if (statusRow && statusRow.classList.contains('variant-item__status')) {
          fragment.appendChild(statusRow.cloneNode(true));
        }
      });
      tableBody.appendChild(fragment);
    }
  }

  /**
   * Dispatch event when all pages are loaded
   */
  dispatchAllPagesLoaded() {
    console.log('All pages loaded, total pages:', this.totalPages);
    document.dispatchEvent(new CustomEvent('infinite-list-all-loaded', { 
      detail: { 
        totalPages: this.totalPages,
        loadedPages: Array.from(this.loadedPages)
      } 
    }));
  }
}

customElements.define('infinite-list', InfiniteList);